// src/components/TIMEBAR/IntegratedTimeBar.js
import React, { useState, useEffect, useRef, useCallback, forwardRef, useImperativeHandle } from 'react';
import './IntegratedTimeBar.css';
import GlobalTimeIndicator from './GlobalTimeIndicator';
import GlobalRobotEvents from './GlobalRobotEvents';
import GenericRobotEvents from './GenericRobotEvents';
import { parseEvents } from './parseEvents';
import { useAppStore } from '../../store/appStore';

const IntegratedTimeBar = forwardRef(({
  maxTime = 1800,
  onTimeUpdate,
  onVideoSelect,
  timeframe = "11AM",
  csvFiles = []
}, ref) => {
  // 从 Zustand 获取全局状态
  const currentGlobalTime = useAppStore(state => state.videoState.currentTime);
  const isGlobalPlaying = useAppStore(state => state.videoState.isPlaying);
  const activeRobotId = useAppStore(state => state.selection.activeRobotId);
  const activeEventId = useAppStore(state => state.selection.activeEventId);
  const updateVideoState = useAppStore(state => state.updateVideoState);
  
  // 本地状态
  const [currentTime, setCurrentTime] = useState(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedRows, setSelectedRows] = useState({});
  const [timeMarkers, setTimeMarkers] = useState([]);
  const [indicatorTooltip, setIndicatorTooltip] = useState('00:00');
  const [timelineEvents, setTimelineEvents] = useState({});
  const [showGlobalEvents, setShowGlobalEvents] = useState(true);
  
  // 引用
  const timelineContainerRef = useRef(null);
  const timeIndicatorRef = useRef(null);
  const timeMarkerRefs = useRef({});
  const dragStartRef = useRef(null);
  const isDraggingRef = useRef(false);
  const activeRowRef = useRef(null);
  const activeRobotIdRef = useRef(activeRobotId);
  const isComponentMounted = useRef(true);
  const rafIdRef = useRef(null);
  
  // 格式化时间显示
  const formatTime = (seconds) => {
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${minutes.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };
  
  // 优化时间指示器更新函数
  const updateTimeIndicator = useCallback((time) => {
    if (!timeIndicatorRef.current || !isComponentMounted.current) return;
    
    const normalizedTime = Math.min(Math.max(0, time), maxTime);
    const percentage = (normalizedTime / maxTime) * 100;
    
    // 使用 setTimeout 实现 30fps
    setTimeout(() => {
      if (timeIndicatorRef.current) {
        timeIndicatorRef.current.style.transform = `translateX(${percentage}%)`;
      }
    }, 1000 / 30); // 30fps
    
    setIndicatorTooltip(formatTime(normalizedTime));
  }, [maxTime]);
  
  // 确保时间指示器初始位置正确
  useEffect(() => {
    if (timeIndicatorRef.current) {
      timeIndicatorRef.current.style.left = '0%';
      setIndicatorTooltip('00:00');
    }
  }, []);
  
  // 高亮活跃行
  const highlightActiveRow = useCallback((index) => {
    // 移除之前的活跃状态
    if (activeRowRef.current) {
      activeRowRef.current.classList.remove('timeline-row-active');
    }
    
    // 设置新的活跃行
    const rows = document.querySelectorAll('.timeline-row');
    if (rows && rows.length > index) {
      const row = rows[index + 1]; // +1 是因为第一行是全局行
      if (row) {
        row.classList.add('timeline-row-active');
        activeRowRef.current = row;
      }
    }
  }, []);
  
  // 初始化所有行为已选中状态
  useEffect(() => {
    const initialSelectedRows = {};
    for (let i = 0; i < csvFiles.length; i++) {
      initialSelectedRows[i] = true;
    }
    setSelectedRows(initialSelectedRows);
  }, [csvFiles]);

  // 创建时间标记
  useEffect(() => {
    if (maxTime <= 0) return;
    
    const interval = maxTime <= 180 ? 15 : maxTime <= 600 ? 60 : 300;
    const markers = [];
    
    for (let time = 0; time <= maxTime; time += interval) {
      markers.push({
        time,
        position: (time / maxTime) * 100
      });
    }
    
    setTimeMarkers(markers);
  }, [maxTime]);
  
  // 解析事件数据
  useEffect(() => {
    if (!isComponentMounted.current) return;
    
    const fetchEvents = async () => {
      // 清除现有事件
      setTimelineEvents({});
      
      // 获取每个机器人的事件
      const newEvents = {};
      
      for (let i = 0; i < csvFiles.length; i++) {
        try {
          const response = await fetch(csvFiles[i].replace('map', 'events'));
          if (!response.ok) {
            console.warn(`无法加载机器人 ${i+1} 的事件：${response.status}`);
            continue;
          }
          
          const csvText = await response.text();
          parseEvents(csvText, (parsedEvents) => {
            if (isComponentMounted.current) {
              // +1 因为机器人ID是从1开始的
              newEvents[i + 1] = parsedEvents;
              setTimelineEvents(prev => ({...prev, [i + 1]: parsedEvents}));
            }
          }, maxTime, timeframe);
        } catch (error) {
          console.error(`加载机器人 ${i+1} 事件时出错:`, error);
        }
      }
    };
    
    fetchEvents();
  }, [csvFiles, maxTime, timeframe]);
  
  // 添加全局时间变化监听
  useEffect(() => {
    if (!isDragging) {
      setCurrentTime(currentGlobalTime);
      updateTimeIndicator(currentGlobalTime);
    }
  }, [currentGlobalTime, isDragging, updateTimeIndicator]);
  
  // 监听全局播放状态变化
  useEffect(() => {
    if (!isComponentMounted.current) return;
    setIsPlaying(isGlobalPlaying);
  }, [isGlobalPlaying]);
  
  // 监听activeRobotId变化
  useEffect(() => {
    activeRobotIdRef.current = activeRobotId;
    
    // 当机器人ID变化时，高亮对应行
    if (activeRobotId) {
      highlightActiveRow(activeRobotId - 1);
    }
  }, [activeRobotId, highlightActiveRow]);
  
  // 监听activeRobotId和activeEventId变化
  const isInitialMount = useRef(true);
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    
    if (activeRobotId && activeEventId !== null) {
      // 找到对应的事件
      const events = timelineEvents[activeRobotId] || [];
      const event = events[activeEventId];
      
      if (event && event.startTime) {
        // 更新时间指示器位置
        const normalizedTime = Math.min(Math.max(0, event.startTime), maxTime);
        const percentage = (normalizedTime / maxTime) * 100;
        
        if (timeIndicatorRef.current) {
          timeIndicatorRef.current.style.left = `${percentage}%`;
        }
        
        // 更新内部时间状态
        setCurrentTime(normalizedTime);
      }
    }
  }, [activeRobotId, activeEventId, timelineEvents, maxTime]);
  
  // 组件挂载/卸载处理
  useEffect(() => {
    isComponentMounted.current = true;
    const rafId = rafIdRef.current;
    
    // 清理函数
    return () => {
      isComponentMounted.current = false;
      
      if (rafId) {
        cancelAnimationFrame(rafId);
      }
    };
  }, []);
  
  // 设置全局播放状态
  const setGlobalPlayState = useCallback((playing) => {
    updateVideoState({ isPlaying: playing }, 'timeline');
  }, [updateVideoState]);
  
  // 处理时间指示器拖动
  const handleTimeIndicatorDragMove = useCallback((e) => {
    if (!isDraggingRef.current || !dragStartRef.current || !timelineContainerRef.current) return;
    
    const { width } = dragStartRef.current;
    const rect = timelineContainerRef.current.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    
    // 计算百分比（确保在0-100范围内）
    const percentage = Math.min(Math.max(0, offsetX / width * 100), 100);
    
    // 更新指示器位置
    if (timeIndicatorRef.current) {
      timeIndicatorRef.current.style.left = `${percentage}%`;
    }
    
    // 计算时间
    const time = (percentage / 100) * maxTime;
    
    // 更新状态
    setCurrentTime(time);
    setIndicatorTooltip(formatTime(time));
  }, [maxTime]);
  
  // 处理时间指示器拖动结束
  const handleTimeIndicatorDragEnd = useCallback(() => {
    if (!isDraggingRef.current) return;
    
    setIsDragging(false);
    isDraggingRef.current = false;
    
    // 移除全局事件监听
    document.removeEventListener('mousemove', handleTimeIndicatorDragMove);
    document.removeEventListener('mouseup', handleTimeIndicatorDragEnd);
    
    // 通知父组件时间更新
    if (onTimeUpdate) {
      onTimeUpdate(currentTime);
    }
    
    // 使用Zustand更新全局状态
    updateVideoState({ time: currentTime }, 'timeline');
    
    // 自动继续播放
    setTimeout(() => {
      if (isComponentMounted.current) {
        setIsPlaying(true);
        setGlobalPlayState(true);
      }
    }, 300);
  }, [currentTime, onTimeUpdate, setGlobalPlayState, updateVideoState, handleTimeIndicatorDragMove]);
  
  // 处理时间指示器拖动开始
  const handleTimeIndicatorDragStart = useCallback((e) => {
    if (!timelineContainerRef.current) return;
    
    setIsDragging(true);
    isDraggingRef.current = true;
    
    const rect = timelineContainerRef.current.getBoundingClientRect();
    dragStartRef.current = {
      x: e.clientX,
      offsetX: e.clientX - rect.left,
      width: rect.width
    };
    
    // 添加全局事件监听
    document.addEventListener('mousemove', handleTimeIndicatorDragMove);
    document.addEventListener('mouseup', handleTimeIndicatorDragEnd);
    
    // 暂停播放
    setIsPlaying(false);
    setGlobalPlayState(false);
  }, [setGlobalPlayState, handleTimeIndicatorDragMove, handleTimeIndicatorDragEnd]);
  
  // 处理播放/暂停按钮点击
  const handlePlayPauseClick = useCallback(() => {
    const newState = !isPlaying;
    setIsPlaying(newState);
    setGlobalPlayState(newState);
  }, [isPlaying, setGlobalPlayState]);
  
  // 处理轨道点击
  const handleTrackClick = useCallback((e) => {
    if (!timelineContainerRef.current) return;
    
    // 获取点击位置
    const rect = e.currentTarget.getBoundingClientRect();
    const offsetX = e.clientX - rect.left;
    const percentage = offsetX / rect.width;
    
    // 计算时间
    const time = percentage * maxTime;
    
    // 更新状态
    setCurrentTime(time);
    updateTimeIndicator(time);
    
    // 通知父组件
    if (onTimeUpdate) {
      onTimeUpdate(time);
    }
    
    // 使用Zustand更新全局状态
    updateVideoState({ time }, 'timeline');
  }, [maxTime, onTimeUpdate, updateTimeIndicator, updateVideoState]);
  
  // 处理行点击
  const handleRowClick = useCallback((index) => {
    // 高亮点击的行
    highlightActiveRow(index);
    
    // 如果点击的是机器人行
    if (index >= 0) {
      const robotId = index + 1; // 机器人ID从1开始
      
      // 构建视频URL
      const timePart = timeframe === "11AM" ? "day" : "night";
      const videoUrl = `/${timePart}/videos/video${robotId}.mp4`;
      
      // 调用视频选择回调
      if (onVideoSelect) {
        onVideoSelect(videoUrl, currentTime, robotId);
      }
      
      // 使用Zustand更新全局状态
      updateVideoState({ robotId, videoUrl }, 'timeline');
    }
  }, [currentTime, onVideoSelect, highlightActiveRow, timeframe, updateVideoState]);
  
  // 切换行选择状态
  const toggleRowSelection = useCallback((index) => {
    setSelectedRows(prev => {
      const newState = { ...prev, [index]: !prev[index] };
      
      // 通知父组件
      if (onTimeUpdate) {
        onTimeUpdate(currentTime);
      }
      
      return newState;
    });
  }, [currentTime, onTimeUpdate]);
  
  // 处理事件点击
  const handleEventClick = useCallback((time, robotId, eventId) => {
    console.log(`时间轴: 事件点击 - 时间: ${time}, 机器人: ${robotId}, 事件ID: ${eventId}`);
    
    // 立即更新UI上的时间指示器位置
    const normalizedTime = Math.min(Math.max(0, time), maxTime);
    const percentage = (normalizedTime / maxTime) * 100;
    
    // 更新时间指示器位置
    if (timeIndicatorRef.current) {
      timeIndicatorRef.current.style.left = `${percentage}%`;
    }
    
    // 设置指示器工具提示
    setIndicatorTooltip(formatTime(normalizedTime));
    
    // 更新内部时间状态
    setCurrentTime(normalizedTime);
    
    // 调用外部回调
    if (onTimeUpdate) {
      onTimeUpdate(normalizedTime);
    }
    
    // 调用视频选择回调
    if (onVideoSelect) {
      const timePart = timeframe === '11AM' ? 'day' : 'night';
      const videoUrl = `/${timePart}/videos/video${robotId}.mp4`;
      onVideoSelect(videoUrl, normalizedTime, robotId, eventId, true);
    }
  }, [maxTime, onTimeUpdate, onVideoSelect, timeframe]);
  
  // 暴露方法给父组件
  useImperativeHandle(ref, () => ({
    // 处理滑块变化
    handleSliderChange: (value) => {
      if (!isComponentMounted.current) return;
      
      setCurrentTime(value);
      updateTimeIndicator(value);
    },
    
    // 更新时间线选择
    updateTimelineSelection: (index, isSelected) => {
      if (!isComponentMounted.current) return;
      
      setSelectedRows(prev => ({
        ...prev,
        [index]: isSelected
      }));
    },
    
    // 重置时间框架
    resetTimeframe: (newTimeframe) => {
      // 当时间框架变化时重置事件数据
      setTimelineEvents({});
    },
    
    // 获取当前时间
    getCurrentTime: () => currentTime,
    
    // 获取当前选择的行
    getSelectedRows: () => selectedRows,
    
    // 设置播放状态
    setPlayState: (playing) => {
      setIsPlaying(playing);
    },

    // 通过ID查找并高亮特定事件
    highlightEvent: (robotId, eventId) => {
      if (!isComponentMounted.current) return;
  
  const eventElement = document.getElementById(`timeline-event-robot${robotId}-${eventId}`);
  if (eventElement) {
    // 移除所有事件的激活状态
    document.querySelectorAll('.timeline-event').forEach(el => {
      el.classList.remove('active');
    });
    
    // 添加激活状态到当前事件
    eventElement.classList.add('active');
    
    // 查找最近的可滚动容器
    const row = eventElement.closest('.timeline-row');
    if (row) {
      row.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest'
      });
      
      // 计算滚动位置，使事件居中
      const eventRect = eventElement.getBoundingClientRect();
      const rowRect = row.getBoundingClientRect();
      const scrollLeft = eventElement.offsetLeft - (rowRect.width / 2) + (eventRect.width / 2);
      
      // 滚动到该位置
      row.scrollLeft = scrollLeft;
    }
  }
},

  }));
  
  // 渲染时间标记
  const renderTimeMarkers = () => {
    return timeMarkers.map(({ time, position }) => (
      <div
        key={`marker-${time}`}
        className="time-marker"
        style={{ left: `${position}%` }}
        ref={el => timeMarkerRefs.current[time] = el}
      >
        <div className="time-marker-label" style={{ transform: 'translateX(-50%)' }}>
          {formatTime(time)}
        </div>
      </div>
    ));
  };
  
  // 渲染时间线行
  const renderTimelineRows = () => {
    const rows = [];
    
    // 全局时间线
    rows.push(
      <div
        key="global-timeline"
        className="timeline-row"
        onClick={() => handleRowClick(-1)}
      >
        <div className="timeline-title">
          <span className={isPlaying ? 'playing-indicator' : ''}>
            Global
          </span>
        </div>
        <div className="timeline-content">
          <div
            className="timeline-track"
            onClick={handleTrackClick}
          >
            <div className="time-markers">
              {renderTimeMarkers()}
            </div>
            {/* 全局事件 */}
            {showGlobalEvents && (
              <GlobalRobotEvents
                maxTime={maxTime}
                events={Object.values(timelineEvents).flat()}
                onEventClick={handleEventClick}
              />
            )}
            {/* 时间指示器（与robot行一致） */}
            <div
              className="time-indicator"
              style={{ left: `${(currentTime / maxTime) * 100}%` }}
            >
              <div className="indicator-bar"></div>
              <div className="indicator-time-label" style={{ position: 'absolute', top: '-10px', left: '50%', transform: 'translateX(-50%)', background: 'rgba(18, 67, 212, 0.75)', color: '#fff', padding: '2px 6px', borderRadius: '4px', fontSize: '12px' }}>
                {formatTime(currentTime)}
              </div>
            </div>
          </div>
        </div>
      </div>
    );
    
    // 机器人时间线
    for (let i = 0; i < csvFiles.length; i++) {
      const robotId = i + 1; // 机器人ID从1开始
      const isSelected = selectedRows[i] !== false; // 默认选中
      
      rows.push(
        <div
          key={`robot-${robotId}`}
          className={`timeline-row ${!isSelected ? 'timeline-row-hidden' : ''} ${
            activeRobotIdRef.current === robotId ? 'timeline-row-active' : ''
          }`}
          onClick={() => handleRowClick(i)}
        >
          <div className="timeline-title">
            <span>Robot {robotId}</span>
          </div>
          <div className="timeline-content">
            <div
              className="timeline-track"
              onClick={handleTrackClick}
            >
              {/* 机器人事件 */}
              <GenericRobotEvents
                robotId={robotId}
                onEventClick={handleEventClick}
                maxTime={maxTime}
                timeframe={timeframe}
                activeEventId={activeRobotId === robotId ? activeEventId : null}
              />
              
              {/* 时间指示器 */}
              <div
                className="time-indicator"
                style={{ left: `${(currentTime / maxTime) * 100}%` }}
              >
                <div className="indicator-bar"></div>
              </div>
            </div>
          </div>
        </div>
      );
    }
    
    return rows;
  };
  
  return (
    <div className="integrated-timebar">
      {/* 时间线容器 */}
      <div className="timeline-container" ref={timelineContainerRef}>
        {renderTimelineRows()}
      </div>
    </div>
  );
});

export default IntegratedTimeBar;